import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import {
  Container,
  CssBaseline,
  Paper,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { useAuth } from '@clerk/react';
import {
  getClerkInvitationRedirect,
  hasClerkInvitationTicket,
  hasDashboardAdminRole,
} from './utils/auth';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import ClientPage from './pages/ClientPage';
import InvitationPage from './pages/InvitationPage';
import LoginPage from './pages/LoginPage';
import OrganizationManagementPage from './pages/OrganizationManagementPage';
import Navbar from './components/Navbar';
import AccountMenu from './components/AccountMenu';
import AuthTokenBridge from './components/AuthTokenBridge';
import DashboardLoadingScreen from './components/DashboardLoadingScreen';
import StupaSupport from './pages/StupaSupport';
// Removed StupaDetails import - now using popup modal in TerrainMap

// Create a custom theme with ice and water inspired colors
export const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3', // Ice blue
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#90caf9', // Light blue
      light: '#bbdefb',
      dark: '#42a5f5',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
  },
});
const LoadingAuth = () => (
  <DashboardLoadingScreen loadingMessage="Checking dashboard access..." />
);

const ClerkInvitationRedirect = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const invitationUrl = getClerkInvitationRedirect(
    location.pathname,
    location.search,
    location.hash
  );

  if (invitationUrl) {
    return <Navigate to={invitationUrl} replace />;
  }

  return <>{children}</>;
};

const NoSponsorAccess = () => (
  <Container maxWidth="sm" sx={{ minHeight: '100vh', pt: 16 }}>
    <Paper sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h4" gutterBottom>
        No sponsor dashboard is assigned
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        This account can only open the company dashboard it was invited to.
        Ask a NILUS administrator to send a sponsor invitation.
      </Typography>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <AccountMenu />
      </div>
    </Paper>
  </Container>
);

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn, sessionClaims, orgSlug } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <LoadingAuth />;
  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!hasDashboardAdminRole(sessionClaims)) {
    return orgSlug
      ? <Navigate to="/dashboard/company" replace />
      : <NoSponsorAccess />;
  }

  return <>{children}</>;
};

const PartnerDashboardRoute = () => {
  const { isLoaded, isSignedIn, sessionClaims, orgId, orgSlug } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <LoadingAuth />;
  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const isAdmin = hasDashboardAdminRole(sessionClaims);
  if (isAdmin) {
    return <Navigate to="/dashboard/operations" replace />;
  }
  if (!isAdmin && (!orgId || !orgSlug)) {
    return <NoSponsorAccess />;
  }
  return <ClientPage sponsor={orgSlug!} />;
};

const CompanyAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn, orgId, orgRole } = useAuth();
  const location = useLocation();
  if (!isLoaded) return <LoadingAuth />;
  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!orgId) return <NoSponsorAccess />;
  if (orgRole !== 'org:admin') return <Navigate to="/dashboard/company" replace />;
  return <>{children}</>;
};

const AuthenticatedLanding = () => {
  const { isLoaded, isSignedIn, sessionClaims, orgId, orgSlug } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <LoadingAuth />;
  if (!isSignedIn) {
    if (hasClerkInvitationTicket(location.search, location.hash)) {
      return (
        <Navigate
          to={`/invitation${location.search}${location.hash}`}
          replace
        />
      );
    }
    return <Navigate to="/login" replace />;
  }
  if (hasDashboardAdminRole(sessionClaims)) {
    return <Navigate to="/dashboard/operations" replace />;
  }
  if (!orgId || !orgSlug) return <NoSponsorAccess />;

  return <Navigate to="/dashboard/company" replace />;
};

const CustomerPreviewRoute = () => {
  const { companyCode } = useParams<{ companyCode: string }>();
  if (!companyCode) return <Navigate to="/dashboard/operations" replace />;
  return <ClientPage sponsor={companyCode} preview />;
};

function App() {

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthTokenBridge>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ClerkInvitationRedirect>
            <Navbar />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/donate/:id" element={<StupaSupport />} />
              <Route path="/support/:id" element={<StupaSupport />} />
              <Route path="/login/*" element={<LoginPage />} />
              <Route path="/invitation/*" element={<InvitationPage />} />
              <Route path="/admin/login/*" element={<Navigate to="/login" replace />} />
              <Route path="/dashboard" element={<AuthenticatedLanding />} />
              <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
              <Route path="/client" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/dashboard/operations"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />
              <Route path="/admin/dashboard" element={<Navigate to="/dashboard/operations" replace />} />

              <Route path="/dashboard/company" element={<PartnerDashboardRoute />} />
              <Route path="/partner-dashboard" element={<Navigate to="/dashboard/company" replace />} />
              <Route
                path="/dashboard/customers/:companyCode"
                element={
                  <AdminRoute>
                    <CustomerPreviewRoute />
                  </AdminRoute>
                }
              />
              <Route
                path="/dashboard/users"
                element={
                  <CompanyAdminRoute>
                    <OrganizationManagementPage />
                  </CompanyAdminRoute>
                }
              />
              <Route path="/company/users" element={<Navigate to="/dashboard/users" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ClerkInvitationRedirect>
        </Router>
      </AuthTokenBridge>
    </ThemeProvider>
  );
}

export default App; 